/**
 * Per-wall thickness (PRP-SKETCH-FIELD-2 item 2, E1, E6). A room's `walls` map is
 * keyed by the id of the corner a wall starts at. A wall with no entry is the
 * policy's `interior_wall_in` (the data contract), so the backend and the core read
 * a missing entry the same way whatever the device's plan settings say.
 *
 * Corner edits that create, merge or renumber corners keep the map in step through
 * the helpers here.
 */
import { POLICY, inToFt } from './policy';
import type { Plan, Room } from './types';

/** A wall's stored thickness, inches. */
export function wallIn(r: Room, vid: number): number {
  const w = r.walls?.[vid];
  return w && Number.isFinite(w.thicknessIn) ? w.thicknessIn : POLICY.interior_wall_in;
}

/** A wall's stored thickness, feet. */
export const wallFtOf = (r: Room, vid: number): number => inToFt(wallIn(r, vid));

/** Store a wall's thickness. The policy default is stored as no entry. */
export function setWallIn(r: Room, vid: number, inches: number): void {
  if (!r.walls) r.walls = {};
  if (inches === POLICY.interior_wall_in) delete r.walls[vid];
  else r.walls[vid] = { thicknessIn: inches };
}

/** Wall `to` takes wall `from`'s thickness (a break splits a wall into two of the same). */
export function copyWall(r: Room, from: number, to: number): void {
  if (!r.walls) return;
  const w = r.walls[from];
  if (w) r.walls[to] = { ...w }; else delete r.walls[to];
}

/** Wall `from` is now the wall starting at `to` (a corner was inserted or merged). */
export function moveWall(r: Room, from: number, to: number): void {
  if (!r.walls) return;
  const w = r.walls[from];
  delete r.walls[from];
  if (w) r.walls[to] = w; else delete r.walls[to];
}

/** Renumber every entry through `map` (old corner id -> new). */
export function remapWalls(r: Room, map: Map<number, number>): void {
  if (!r.walls) return;
  const out: Record<number, { thicknessIn: number }> = {};
  for (const [k, w] of Object.entries(r.walls)) {
    const to = map.get(Number(k));
    if (to != null) out[to] = { ...w };
  }
  r.walls = out;
}

/** Drop entries whose corner no longer exists. */
export function pruneWalls(r: Room): void {
  if (!r.walls) return;
  const ids = new Set(r.corners.map((c) => c.id));
  for (const k of Object.keys(r.walls)) if (!ids.has(Number(k))) delete r.walls[Number(k)];
}

/**
 * A new room's walls: an empty map (it is a FIELD-2 room, never migrated), with
 * every wall stamped at the plan's interior default when that differs from the
 * policy's (G2: new walls use the plan default).
 */
export function stampNewRoom(plan: Plan, r: Room): void {
  r.walls = {};
  const d = plan.defaults.interiorWallIn;
  if (d == null || d === POLICY.interior_wall_in) return;
  for (const c of r.corners) r.walls[c.id] = { thicknessIn: d };
}
